import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { env } from '../utils/environment';
import {
  HomeIcon,
  UserGroupIcon,
  ClockIcon,
  CalendarIcon,
  CogIcon,
  ChartBarIcon,
  UserIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  TicketIcon,
  UsersIcon,
  InboxStackIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';

interface SidebarProps {
  userRole: 'admin' | 'member' | 'patient';
  /** Called when a nav link is clicked — used to close the mobile drawer. */
  onNavigate?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ userRole, onNavigate }) => {
  const location = useLocation();
  const { user } = useAuth();

  const isActive = (path: string) => {
    // For dashboard routes, use exact matching
    if (path === '/admin' || path === '/member' || path === '/patient') {
      return location.pathname === path;
    }
    // For sub-routes, use prefix matching
    return location.pathname.startsWith(path);
  };

  const getNavItems = () => {
    switch (userRole) {
      case 'admin':
        return [
          { name: 'Dashboard', path: '/admin', icon: HomeIcon },
          { name: 'User Management', path: '/admin/users', icon: UserGroupIcon },
          { name: 'Room Management', path: '/admin/rooms', icon: BuildingOfficeIcon },
          { name: 'All Bookings', path: '/admin/bookings', icon: CalendarIcon },
          { name: 'Notifications', path: '/admin/notifications', icon: BellAlertIcon },
          { name: 'Billing', path: '/admin/billing', icon: CurrencyDollarIcon },
          { name: 'Analytics', path: '/admin/analytics', icon: ChartBarIcon },
          { name: 'Global Settings', path: '/admin/settings', icon: CogIcon },
        ];
      case 'member': {
        const memberNav = [
          { name: 'Dashboard', path: '/member', icon: HomeIcon },
          { name: 'Availability', path: '/member/availability', icon: ClockIcon },
          { name: 'My Bookings', path: '/member/bookings', icon: CalendarIcon },
        ];
        if (user?.requires_booking_approval) {
          memberNav.push({ name: 'Booking Requests', path: '/member/booking-requests', icon: InboxStackIcon });
        }
        if (user?.is_public === false) {
          memberNav.push({ name: 'Invite Codes', path: '/member/invite-codes', icon: TicketIcon });
        }
        memberNav.push(
          { name: 'Book a Pod', path: '/member/book-pod', icon: BuildingOfficeIcon },
          { name: 'Billing', path: '/member/billing', icon: CurrencyDollarIcon },
          { name: 'Profile', path: '/member/profile', icon: UserIcon },
          { name: 'Settings', path: '/member/settings', icon: CogIcon },
        );
        return memberNav;
      }
      case 'patient':
        return [
          { name: 'Dashboard', path: '/patient', icon: HomeIcon },
          { name: 'Find a Clinician', path: '/clinicians', icon: MagnifyingGlassIcon },
          { name: 'My Clinicians', path: '/patient/my-clinicians', icon: UsersIcon },
          { name: 'Book Appointment', path: '/patient/book', icon: CalendarIcon },
          { name: 'My Appointments', path: '/patient/appointments', icon: ClockIcon },
          { name: 'Profile', path: '/patient/profile', icon: UserIcon },
        ];
      default:
        return [];
    }
  };

  const navItems = getNavItems();

  const getRoleLabel = () => {
    switch (userRole) {
      case 'admin':
        return 'Admin Portal';
      case 'member':
        return 'Clinician Portal';
      case 'patient':
        return 'Client Portal';
      default:
        return '';
    }
  };

  return (
    <div className="bg-clinic-primary-dark shadow-clinic-md h-full w-64 flex flex-col">
      {/* Logo Section — white cap so the sage-green logo reads against the green chrome.
          Matches the Header's h-20 + bottom border so the top bar reads as one strip. */}
      <div className="flex items-center justify-center h-20 px-4 bg-white border-b border-clinic-primary-lighter">
        <Link to="/" className="flex items-center">
          <img
            src="/logos/logo-primary.png"
            alt="Clinic Studios"
            className="h-12 w-auto object-contain"
          />
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 pt-6 pb-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);

          return (
            <Link
              key={item.path}
              to={item.path}
              data-guide={`nav-${item.path.split('/').filter(Boolean).slice(-1)[0]}`}
              onClick={onNavigate}
              aria-current={active ? 'page' : undefined}
              className={`
                flex items-center px-4 py-3 rounded-lg
                font-body text-sm font-medium
                transition-all duration-150
                ${
                  active
                    ? 'bg-clinic-cream text-clinic-dark shadow-clinic-sm font-semibold'
                    : 'text-clinic-cream hover:bg-white/10 hover:text-white'
                }
              `}
            >
              <Icon className={`h-5 w-5 mr-3 flex-shrink-0 ${active ? 'text-clinic-primary' : 'text-clinic-cream/80'}`} />
              <span className="truncate">{item.name}</span>
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-white/15">
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs font-medium text-clinic-cream/70 tracking-wide uppercase">
            {getRoleLabel()}
          </span>
          <span className="text-[11px] text-clinic-cream/45 tracking-wide">
            Clinic Studios v{env.version}
          </span>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
